import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, MapPin, Pencil, Trash2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fmtDate, brl, fileUrl } from "../lib/api";
import { useAthletes } from "../lib/hooks";
import { Loader, PillTabs } from "../components/common";
import { StageForm, StageStatus } from "./Stages";
import RegistrationsTab from "../components/stage/RegistrationsTab";
import BracketTab from "../components/stage/BracketTab";
import PointsTab from "../components/stage/PointsTab";

export default function StageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stage, setStage] = useState(null);
  const [level, setLevel] = useState(null);
  const [tab, setTab] = useState("inscricoes");
  const [edit, setEdit] = useState(false);
  const [athletes, reloadAthletes] = useAthletes();
  const load = () => api.get(`/stages/${id}`).then((r) => { setStage(r.data); setLevel((l) => l || r.data.levels[0]); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async () => {
    if (!window.confirm("Excluir esta etapa e todas as inscrições e chaves?")) return;
    try {
      await api.delete(`/stages/${id}`);
      toast.success("Etapa excluída");
      navigate("/app/etapas");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!stage) return <Loader />;
  const tabs = [{ value: "inscricoes", label: "Inscrições" }, { value: "chaves", label: "Chaves / Jogos" }, { value: "pontos", label: "Pontuação" }];
  return (
    <div>
      <button onClick={() => navigate("/app/etapas")} data-testid="back-to-stages" className="mb-4 flex items-center gap-1 text-sm text-[#94A3B8] hover:text-white"><ArrowLeft className="h-4 w-4" />Etapas</button>
      <div className="card-x relative mb-6 overflow-hidden p-6 rise">
        {stage.cover_path && <img src={fileUrl(stage.cover_path)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />}
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3"><span className="font-display text-4xl font-black italic text-[#E4F222]">#{stage.number}</span><StageStatus status={stage.status} /></div>
            <h1 data-testid="stage-title" className="h-display mt-1 text-4xl sm:text-5xl">{stage.name}</h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#94A3B8]">
              <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{fmtDate(stage.date)}</span>
              {stage.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{stage.location}</span>}
              {stage.price > 0 && <span>{brl(stage.price)} por atleta</span>}
            </div>
            {stage.description && <p className="mt-3 max-w-2xl text-sm text-[#94A3B8]">{stage.description}</p>}
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button data-testid="edit-stage-btn" onClick={() => setEdit(true)} className="btn-ghost flex items-center gap-1"><Pencil className="h-4 w-4" />Editar</button>
              <button data-testid="delete-stage-btn" onClick={remove} className="btn-ghost text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 eyebrow">Nível</div>
      <PillTabs testid="level-tab" tabs={stage.levels.map((l) => ({ value: l, label: `${l} (${stage.counts[l] || 0})` }))} value={level} onChange={setLevel} />
      <div className="mt-4 flex gap-6 border-b border-[#232B3E]">
        {tabs.map((t) => (
          <button key={t.value} data-testid={`stage-tab-${t.value}`} onClick={() => setTab(t.value)}
            className={`-mb-px border-b-2 pb-3 text-sm font-semibold ${tab === t.value ? "border-[#E4F222] text-white" : "border-transparent text-[#64748B]"}`}>{t.label}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "inscricoes" && <RegistrationsTab stage={stage} level={level} athletes={athletes} reloadAthletes={reloadAthletes} onChange={load} />}
        {tab === "chaves" && <BracketTab stage={stage} level={level} />}
        {tab === "pontos" && <PointsTab stage={stage} level={level} athletes={athletes} />}
      </div>
      <StageForm open={edit} onOpenChange={setEdit} stage={stage} onSaved={load} />
    </div>
  );
}
